import React from "react";

import { datadogRum } from "@datadog/browser-rum";
import type {
  StripeElements,
  StripeElementsOptionsMode,
  StripePaymentMethodMessagingElementOptions,
} from "@stripe/stripe-js";

import { useStripeContext } from "~/components/stripe/stripe.hooks";
import { formatStripeAmount } from "~/components/stripe/stripe.utils";

import {
  STRIPE_PAYMENT_ELEMENT_STYLES,
  STRIPE_PAYMENT_ELEMENT_FONTS,
} from "./stripe.constants";

type PaymentMethodMessagingElementProps = {
  amount: number;
  currency: string;
  country: string;
  id?: string;
};

// This is Stripe's PaymentMethodMessagingElement wrapper component
// See: https://docs.stripe.com/elements/payment-method-messaging
const PaymentMethodMessagingElement = ({
  amount,
  country,
  currency,
  id = "payment-method-messaging-element",
}: PaymentMethodMessagingElementProps) => {
  // ID selector of element where we're going to render messaging element
  const domSelector = `#${id}`;

  // Stripe elements config options
  const options: StripeElementsOptionsMode = React.useMemo(() => {
    return {
      fonts: STRIPE_PAYMENT_ELEMENT_FONTS,
      appearance: STRIPE_PAYMENT_ELEMENT_STYLES,
      amount: formatStripeAmount(amount),
      mode: "payment",
      currency: currency.toLowerCase(),
    };
  }, [amount, currency]);

  const { stripeInstance: stripe } = useStripeContext();

  // Mount created "Elements['paymentMethodMessaging']" instance into the dom
  const mount = React.useCallback(
    (elements: StripeElements) => {
      const domElement = document.querySelector(domSelector);

      if (!elements || !domElement) {
        return;
      }

      const existingElement = elements.getElement("paymentMethodMessaging");

      if (existingElement) {
        existingElement.destroy();
      }

      try {
        const element = elements.create("paymentMethodMessaging", {
          amount: options.amount,
          currency: options.currency?.toUpperCase(),
          paymentMethodTypes: ["klarna"],
          countryCode: country.toUpperCase(),
        } as StripePaymentMethodMessagingElementOptions);
        element.mount(domSelector);

        return element;
      } catch (error) {
        datadogRum.addError(error);
        return undefined;
      }
    },
    [options.amount, options.currency, country, domSelector],
  );

  // Destroy "Elements['paymentMethodMessaging']" (unmount)
  const destroy = React.useCallback((elements: StripeElements) => {
    if (!elements) {
      return;
    }

    const messagingElement = elements.getElement("paymentMethodMessaging");
    if (messagingElement) {
      messagingElement.destroy();
    }
  }, []);

  // Create "Elements" instance based on stripe elements config options
  const create = React.useCallback(() => {
    if (!stripe) {
      return null;
    }

    return stripe.elements(options);
  }, [stripe, options]);

  // As booking amount changes we need to re-mount messaging element with up-to-date price
  // 1. destroy (if previously mounted)
  // 2. create new Elements instance (with up-to-date amounts)
  // 3. mount "Elements" instance
  const refresh = React.useCallback(() => {
    if (!amount || !currency || !country) {
      return;
    }

    if (prevElements.current) {
      destroy(prevElements.current);
    }
    const newElements = create();
    if (newElements) {
      mount(newElements);
    }
    prevElements.current = newElements;
  }, [amount, currency, country, destroy, create, mount]);

  const prevElements = React.useRef<StripeElements | null>(null);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  return <div id={id} />;
};

export { PaymentMethodMessagingElement };
